// ThreatsSection.tsx
import { lazy, Suspense, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { AppRouteBody } from '@/components/AppRouteBody';
import type { TabOption } from '@/components/chariot-ui';
import { Tabs } from '@/components/chariot-ui';
import { Spinner } from '@/components/ui/spinner';

// Lazy load tabs for code splitting
const ThreatsListTab = lazy(() =>
  import('./ThreatsListTab').then(m => ({ default: m.ThreatsListTab }))
);
const ThreatIntelligenceTab = lazy(() =>
  import('./ThreatIntelligence').then(m => ({
    default: m.ThreatIntelligence,
  }))
);

const THREATS_SECTION_TABS: TabOption[] = [
  { label: 'Exploit Intelligence', value: '' },
  { label: 'Threat Intelligence', value: 'intelligence' },
];

function TabLoadingFallback() {
  return <Spinner size="xl" centered />;
}

/**
 * Top-level Threats section with tabs.
 * URL state synced via `?tab=` query parameter (same pattern as Attacks.tsx).
 */
export function ThreatsSection() {
  const [searchParams, setSearchParams] = useSearchParams();

  const validTabValues = useMemo(
    () => THREATS_SECTION_TABS.map(tab => tab.value),
    []
  );

  const selectedTabValue = useMemo(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && validTabValues.includes(tabFromUrl)) {
      return tabFromUrl;
    }
    return '';
  }, [searchParams, validTabValues]);

  const selectedTab =
    THREATS_SECTION_TABS.find(tab => tab.value === selectedTabValue) ||
    THREATS_SECTION_TABS[0];

  const handleTabSelect = (tab: TabOption) => {
    setSearchParams(
      prev => {
        const newParams = new URLSearchParams(prev);
        if (tab.value) {
          newParams.set('tab', tab.value as string);
        } else {
          newParams.delete('tab');
        }
        return newParams;
      },
      { replace: true }
    );
  };

  return (
    <AppRouteBody
      title="Threats"
      data-tutorial="threats-section"
      tablePadding={false}
    >
      <div
        className="flex flex-col gap-4 overflow-hidden p-6 pb-0"
        data-tutorial="threats-tabs"
      >
        <Tabs
          tabs={[...THREATS_SECTION_TABS]}
          selected={selectedTab}
          variant="primary"
          onSelect={handleTabSelect}
        />
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-6 pt-2">
        {selectedTabValue === '' && (
          <Suspense fallback={<TabLoadingFallback />}>
            <div className="h-full overflow-auto">
              <ThreatsListTab />
            </div>
          </Suspense>
        )}
        {selectedTabValue === 'intelligence' && (
          <Suspense fallback={<TabLoadingFallback />}>
            <ThreatIntelligenceTab />
          </Suspense>
        )}
      </div>
    </AppRouteBody>
  );
}
